// ===========================================
// Cancellation Table Component
// ===========================================
// Table showing cancellation patterns and reasons
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Skeleton,
} from '@mui/material';
import { CancellationRecord } from '../../api/reports.api';
import { formatPercentage } from '../../hooks/useReports';

interface CancellationTableProps {
  data?: CancellationRecord[];
  isLoading: boolean;
}

const CancellationTable: React.FC<CancellationTableProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Cancellation Patterns
        </Typography>
        <Skeleton variant="rectangular" height={300} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Cancellation Patterns
        </Typography>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No cancellation data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  const getCancellationRateColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate < 5) return 'success';
    if (rate < 10) return 'warning';
    return 'error';
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Cancellation Patterns
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track cancellations by week and identify common reasons
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Week</TableCell>
              <TableCell align="right">Cancellations</TableCell>
              <TableCell align="right">Cancellation Rate</TableCell>
              <TableCell>Top Reasons</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((record) => (
              <TableRow key={record.weekNumber}>
                <TableCell>{record.weekLabel}</TableCell>
                <TableCell align="right">{record.totalCancellations}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatPercentage(record.cancellationRate)}
                    color={getCancellationRateColor(record.cancellationRate)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {record.topReasons.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {record.topReasons.slice(0, 3).map((reason, index) => (
                        <Chip
                          key={index}
                          label={`${reason.reason} (${reason.count})`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No reasons provided
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CancellationTable;
